import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArcButton } from "@/components/ArcButton";
import { Plus, Trash2, ChevronUp, ChevronDown, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());

function itemType(it) {
  if (it.random) return "random";
  const to = it.to || "";
  if (to.startsWith("/article/")) return "article";
  if (to.startsWith("/quiz/")) return "quiz";
  return "custom";
}

export function SidebarEditor({ enc, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [articles, setArticles] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/nav/${enc}`).then((r) => setCfg({ hidden: !!r.data.hidden, groups: r.data.groups || [] })).catch(() => {});
  useEffect(() => {
    load();
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/quizzes").then((r) => setQuizzes(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enc]);

  if (!cfg) return <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>Loading…</p>;

  const setGroups = (groups) => setCfg((c) => ({ ...c, groups }));
  const mut = (fn) => setCfg((c) => { const groups = c.groups.map((g) => ({ ...g, items: [...g.items] })); fn(groups); return { ...c, groups }; });

  const addGroup = () => setGroups([...cfg.groups, { id: uid(), label: "New group", items: [] }]);
  const delGroup = (gi) => setGroups(cfg.groups.filter((_, i) => i !== gi));
  const groupLabel = (gi, v) => mut((g) => { g[gi] = { ...g[gi], label: v }; });
  const moveGroup = (gi, d) => { const j = gi + d; if (j < 0 || j >= cfg.groups.length) return; const g = [...cfg.groups]; [g[gi], g[j]] = [g[j], g[gi]]; setGroups(g); };

  const addItem = (gi) => mut((g) => { g[gi].items = [...g[gi].items, { id: uid(), label: "New link", to: "", random: false }]; });
  const delItem = (gi, ii) => mut((g) => { g[gi].items = g[gi].items.filter((_, i) => i !== ii); });
  const setItem = (gi, ii, patch) => mut((g) => { g[gi].items = g[gi].items.map((it, i) => i === ii ? { ...it, ...patch } : it); });
  const moveItem = (gi, ii, d) => mut((g) => { const arr = g[gi].items; const j = ii + d; if (j < 0 || j >= arr.length) return; [arr[ii], arr[j]] = [arr[j], arr[ii]]; });

  const changeType = (gi, ii, type) => {
    if (type === "random") setItem(gi, ii, { random: true, to: "" });
    else if (type === "article") setItem(gi, ii, { random: false, to: `/article/${articles[0]?.slug || ""}` });
    else if (type === "quiz") setItem(gi, ii, { random: false, to: `/quiz/${quizzes[0]?.slug || ""}` });
    else setItem(gi, ii, { random: false, to: "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/nav/${enc}`, { hidden: cfg.hidden, groups: cfg.groups });
      toast.success("Sidebar saved");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset this sidebar to the original default layout?")) return;
    try { const { data } = await api.delete(`/nav/${enc}`); setCfg({ hidden: !!data.hidden, groups: data.groups || [] }); toast.success("Reset to default (remember to Save)"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid={`sidebar-editor-${enc}`}>
      <div className="flex items-center gap-3 rounded-md border p-4" style={{ backgroundColor: "var(--arc-card)" }}>
        <Switch checked={cfg.hidden} onCheckedChange={(v) => setCfg((c) => ({ ...c, hidden: v }))} data-testid={`nav-hidden-${enc}`} />
        <Label>Hide the sidebar entirely for the {enc === "ghoulax" ? "Ghoulax" : "Zyfley"} encyclopedia</Label>
      </div>

      {cfg.groups.map((g, gi) => (
        <div key={g.id} data-testid={`nav-group-${gi}`} className="rounded-md border p-4 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>
          <div className="flex items-center gap-2">
            <Input value={g.label} onChange={(e) => groupLabel(gi, e.target.value)} data-testid={`nav-group-label-${gi}`} className="font-semibold" style={{ backgroundColor: "var(--arc-bg)" }} />
            <button onClick={() => moveGroup(gi, -1)} className="rounded border p-1.5" title="Move up"><ChevronUp size={15} /></button>
            <button onClick={() => moveGroup(gi, 1)} className="rounded border p-1.5" title="Move down"><ChevronDown size={15} /></button>
            <button onClick={() => delGroup(gi)} className="rounded border p-1.5 text-red-600" data-testid={`nav-del-group-${gi}`} title="Delete group"><Trash2 size={15} /></button>
          </div>

          <div className="mt-3 space-y-2 border-l pl-3" style={{ borderColor: "var(--arc-border)" }}>
            {g.items.map((it, ii) => {
              const type = itemType(it);
              return (
                <div key={it.id} data-testid={`nav-item-${gi}-${ii}`} className="grid grid-cols-1 gap-2 rounded-md border p-2 sm:grid-cols-[1fr_120px_1fr_auto] sm:items-center" style={{ backgroundColor: "var(--arc-bg)" }}>
                  <Input value={it.label} onChange={(e) => setItem(gi, ii, { label: e.target.value })} placeholder="Link label" data-testid={`nav-item-label-${gi}-${ii}`} style={{ backgroundColor: "var(--arc-card)" }} />
                  <Select value={type} onValueChange={(v) => changeType(gi, ii, v)}>
                    <SelectTrigger data-testid={`nav-item-type-${gi}-${ii}`} style={{ backgroundColor: "var(--arc-card)" }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="article">Article</SelectItem>
                      <SelectItem value="quiz">Quiz</SelectItem>
                      <SelectItem value="custom">Custom URL</SelectItem>
                      <SelectItem value="random">Random Article</SelectItem>
                    </SelectContent>
                  </Select>
                  {type === "article" && (
                    <Select value={(it.to || "").replace("/article/", "")} onValueChange={(v) => setItem(gi, ii, { to: `/article/${v}` })}>
                      <SelectTrigger data-testid={`nav-item-article-${gi}-${ii}`} style={{ backgroundColor: "var(--arc-card)" }}><SelectValue placeholder="Choose article" /></SelectTrigger>
                      <SelectContent>{articles.map((a) => <SelectItem key={a.slug} value={a.slug}>{a.title}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {type === "quiz" && (
                    <Select value={(it.to || "").replace("/quiz/", "")} onValueChange={(v) => setItem(gi, ii, { to: `/quiz/${v}` })}>
                      <SelectTrigger data-testid={`nav-item-quiz-${gi}-${ii}`} style={{ backgroundColor: "var(--arc-card)" }}><SelectValue placeholder="Choose quiz" /></SelectTrigger>
                      <SelectContent>{quizzes.map((qz) => <SelectItem key={qz.slug} value={qz.slug}>{qz.title}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {type === "custom" && (
                    <Input value={it.to || ""} onChange={(e) => setItem(gi, ii, { to: e.target.value })} placeholder="/path or https://…" data-testid={`nav-item-custom-${gi}-${ii}`} style={{ backgroundColor: "var(--arc-card)" }} />
                  )}
                  {type === "random" && <span className="text-xs italic" style={{ color: "var(--arc-text-2)" }}>Picks a random article</span>}
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveItem(gi, ii, -1)} className="rounded border p-1.5" title="Move up"><ChevronUp size={14} /></button>
                    <button onClick={() => moveItem(gi, ii, 1)} className="rounded border p-1.5" title="Move down"><ChevronDown size={14} /></button>
                    <button onClick={() => delItem(gi, ii)} className="rounded border p-1.5 text-red-600" data-testid={`nav-del-item-${gi}-${ii}`} title="Delete link"><Trash2 size={14} /></button>
                  </div>
                </div>
              );
            })}
            <button onClick={() => addItem(gi)} data-testid={`nav-add-item-${gi}`} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline" style={{ color: "var(--arc-text-2)" }}><Plus size={13} /> Add link</button>
          </div>
        </div>
      ))}

      <ArcButton variant="outline" onClick={addGroup} data-testid="nav-add-group"><Plus size={15} /> Add group</ArcButton>

      <div className="flex flex-wrap gap-2 border-t pt-4">
        <ArcButton variant="ghoulax" onClick={save} disabled={saving} data-testid={`nav-save-${enc}`}>{saving ? "Saving…" : "Save sidebar"}</ArcButton>
        <ArcButton variant="ghost" className="border" onClick={resetDefaults} data-testid={`nav-reset-${enc}`}><RotateCcw size={15} /> Reset to default</ArcButton>
      </div>
    </div>
  );
}
